/**
 * ERP vs project-tool classification.
 * Prefers role fields from GET /api/integrations. Falls back to this catalog
 * so sync pages still work if an older payload is missing role.
 *
 * When a new ERP is registered in app/integrations/catalog.py, keep this list
 * in sync (or rely on the API fields).
 */
export const ROLE_ERP = 'erp';
export const ROLE_PROJECT_TOOL = 'project_tool';

const CATALOG = [
  {
    type: 'quickbooks_online',
    role: ROLE_ERP,
    kind: 'qbo',
    label: 'QuickBooks Online',
    aliases: ['qbo', 'quickbooks', 'quickbook', 'quickbooks online'],
    sendKinds: {
      project_billing: 'qbo-project-billing',
      journal_entry: 'journal-entry',
      ap_invoice: 'ap-invoice',
    },
  },
  {
    type: 'procore',
    role: ROLE_PROJECT_TOOL,
    kind: 'procore',
    label: 'Procore',
    aliases: ['procore'],
    sendKinds: {
      project_billing: 'project-billing',
      ap_invoice: 'ap-invoice',
      direct_cost: 'direct-cost',
    },
  },
];

function normalize(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
}

function specFromCatalog(integrationOrType) {
  const raw = normalize(
    integrationOrType?.integration_type || integrationOrType?.type || integrationOrType
  );
  if (!raw) return null;
  const exact = CATALOG.find((row) => row.type === raw || (row.aliases || []).includes(raw));
  if (exact) return exact;
  return (
    CATALOG.find(
      (row) =>
        (row.type && raw.includes(row.type)) ||
        (row.aliases || []).some((alias) => alias && raw.includes(alias))
    ) || null
  );
}

export function integrationSpec(row) {
  if (row && row.role && row.role_kind) {
    return {
      role: row.role,
      kind: row.role_kind,
      label: row.role_label,
      sendKinds: row.send_kinds || {},
    };
  }
  const spec = specFromCatalog(row);
  if (!spec) {
    return {
      role: null,
      kind: normalize(row?.integration_type || ''),
      label: row?.integration_name || row?.integration_type || '',
      sendKinds: {},
    };
  }
  return {
    role: spec.role,
    kind: spec.kind,
    label: spec.label,
    sendKinds: spec.sendKinds || {},
  };
}

export function isErpIntegration(row) {
  return integrationSpec(row).role === ROLE_ERP;
}

export function isProjectToolIntegration(row) {
  return integrationSpec(row).role === ROLE_PROJECT_TOOL;
}

export function isSyncableIntegration(row) {
  const role = integrationSpec(row).role;
  return role === ROLE_ERP || role === ROLE_PROJECT_TOOL;
}

export function integrationKind(row) {
  return integrationSpec(row).kind;
}

export function integrationLabel(row) {
  return integrationSpec(row).label || row?.integration_name || row?.integration_type || '';
}

export function erpSendKind(row, objectKey) {
  if (!isErpIntegration(row)) return null;
  return integrationSpec(row).sendKinds?.[objectKey] || null;
}

export function sendKindFor(row, objectKey, fallback) {
  const kind = integrationSpec(row).sendKinds?.[objectKey];
  return kind || fallback || null;
}
